import React, { useState, useEffect } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import * as yup from 'yup';
import FormControl from '@mui/material/FormControl';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ClearIcon from '@mui/icons-material/Clear';
import { useFormik } from 'formik';
import { styled } from '@mui/material/styles';

const CreateProcess = (props) => {
  const {
    is_process_modal_open,
    handleProcessModal,
    is_loading,
    handleProcessSubmit,
    handleProcessUpdate,
    c_action,
    process_id,
    selected_data
  } = props;

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [previewImage, setPreviewImage] = useState(null);
  const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
  });

  const handleProcessCloseModalChange = () => {
    formik.resetForm();
    setPreviewImage(null);
    handleProcessModal(false);
  };

  const validationSchema = yup.object().shape({
    process_name: yup.string().trim().required('Process Name is required.'),
    process_email: yup.string()
      .test('valid-emails', 'Invalid email address(es).', function (value) {
        if (!value) return false;
        const emails = value.split(/[\s,]+/);
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emails.every(email => emailRegex.test(email));
      })
      .required('Email ID is required.'),
    display_order: yup.number().required('Display Order is required.'),
  });

  const onSubmit = async (values) => {
    let res_create, res_update;
    if (c_action === "UPDATE") {
      const payload = { process_id, updated_data: values };
      res_update = await handleProcessUpdate(payload);
    } else {
      res_create = await handleProcessSubmit(values);
    }

    if ((res_create || res_update) && !(res_create?.error || res_update?.error)) {
      formik.resetForm();
      setPreviewImage(null);
      handleProcessModal(false);
      window.location.reload();
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      process_name: c_action === "UPDATE" ? selected_data.name : '',
      process_email: c_action === "UPDATE" ? (selected_data.process_email || '') : '',
      display_order: c_action === "UPDATE" ? selected_data.display_order : '',
      process_logo: null,
    },
    validationSchema: validationSchema,
    onSubmit,
  });

  useEffect(() => {
    if (formik.values.process_logo) {
      const fileReader = new FileReader();
      fileReader.onload = () => setPreviewImage(fileReader.result);
      fileReader.readAsDataURL(formik.values.process_logo);
    } else {
      setPreviewImage(null);
    }
  }, [formik.values.process_logo]);

  return (
    <Dialog
      fullScreen={fullScreen}
      open={is_process_modal_open}
      onClose={handleProcessCloseModalChange}
      aria-labelledby="responsive-dialog-title"
      component="form"
      onSubmit={formik.handleSubmit}
      sx={{ '& .MuiDialog-paper': { width: '310px' } }}
    >
      <DialogTitle
        id="responsive-dialog-title"
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        {(c_action === 'UPDATE' && 'Update Process') || (c_action === 'CREATE' && 'Create Process')}
        <IconButton size="medium" onClick={handleProcessCloseModalChange}>
          <CloseOutlinedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          '& .MuiTextField-root,.MuiFormControl-root': {
            width: { md: '30ch', xs: '100%' },
            fontSize: '14px',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: '6px', alignItems: 'center' }}>

          <TextField
            label="Process Name"
            variant="outlined"
            size="small"
            name="process_name"
            value={formik.values.process_name}
            onChange={formik.handleChange('process_name')}
            onBlur={formik.handleBlur('process_name')}
            error={formik.touched.process_name && Boolean(formik.errors.process_name)}
            helperText={formik.touched.process_name && formik.errors.process_name}
          />

          <TextField
            label="Display Order"
            variant="outlined"
            size="small"
            type="number"
            name="display_order"
            value={formik.values.display_order}
            onChange={formik.handleChange('display_order')}
            onBlur={formik.handleBlur('display_order')}
            error={formik.touched.display_order && Boolean(formik.errors.display_order)}
            helperText={formik.touched.display_order && formik.errors.display_order}
          />

          <TextField
            label="Email ID *"
            variant="outlined"
            size="small"
            name="process_email"
            value={formik.values.process_email}
            onChange={formik.handleChange('process_email')}
            onBlur={formik.handleBlur('process_email')}
            error={formik.touched.process_email && Boolean(formik.errors.process_email)}
            helperText={formik.touched.process_email && formik.errors.process_email}
            sx={{ mb: 2 }}
          />

          <FormControl error={formik.touched.process_logo && Boolean(formik.errors.process_logo)} fullWidth>
            <Button
              component="label"
              disableElevation
              variant="contained"
              startIcon={<CloudUploadIcon />}
              sx={{ fontSize: { xs: '12px', sm: '14px' } }}
            >
              {is_loading && formik.values.process_logo ? 'Uploading...' : 'Process Logo'}
              <VisuallyHiddenInput
                type="file"
                accept="image/*"
                onChange={(event) => {
                  const file = event.target.files[0];
                  formik.setFieldValue('process_logo', file);
                }}
              />
            </Button>

            {previewImage && (
              <Box
                sx={{
                  marginTop: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <img
                  src={previewImage}
                  alt="Preview"
                  style={{
                    width: '50px',
                    height: '50px',
                    objectFit: 'contain',
                    borderRadius: '1px',
                    border: '1px solid #ddd',
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => formik.setFieldValue('process_logo', null)}
                  sx={{ fontSize: '18px' }}
                >
                  <ClearIcon />
                </IconButton>
              </Box>
            )}

            {!previewImage && c_action === "UPDATE" && selected_data.process_logo && (
              <Box sx={{ marginTop: 2, textAlign: 'center' }}>
                <img
                  src={selected_data.process_logo}
                  alt="Current Process Logo"
                  style={{
                    width: '50px',
                    height: '50px',
                    objectFit: 'contain',
                    borderRadius: '8px',
                    border: '1px solid #ddd',
                  }}
                />
                <Typography sx={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
                  Current Logo
                </Typography>
              </Box>
            )}
          </FormControl>
        </Box >
      </DialogContent >

      <DialogActions
        sx={{
          padding: { xs: '12px', sm: '8px 16px 24px 24px' },
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          width: { md: '30ch', xs: '100%' },
        }}
      >
        <Button
          variant="outlined"
          color="error"
          onClick={handleProcessCloseModalChange}
          sx={{ fontSize: { xs: '12px', sm: '14px' }, borderColor: "#ff6100", color: "#4c4e52" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          type="submit"
          sx={{ fontSize: { xs: '12px', sm: '14px' }, backgroundColor: "#ff6100" }}
        >
          {(c_action === 'UPDATE' && 'Update') || (c_action === 'CREATE' && 'Submit')}
        </Button>
      </DialogActions>
    </Dialog >
  );
};

export default CreateProcess;
